import type { DocumentTag } from '../../constants/document-tags'
import type { ContractDirection, ContractStatus } from '../../enums'

import { COMMON } from './common'
import { TERMS } from './terms'

/**
 * A project's finances: contracts, installments, invoices, approvals,
 * payments, and the margin they leave. The customer's wording of the same
 * things is `CUSTOMER_FINANCE`; the stamp on an invoice reads `STAMP`.
 */

/** Domain nouns the finance surfaces build labels out of. */
export const FINANCE_TERMS = {
  contract: 'Vertrag',
  contractPlural: 'Verträge',
  supplement: 'Nachtrag',
  installment: 'Abschlag',
  installmentPlural: 'Abschläge',
  milestone: 'Meilenstein',
  milestonePlural: 'Meilensteine',
  invoice: 'Rechnung',
  invoicePlural: 'Rechnungen',
  paymentPlural: 'Zahlungen',
  deduction: 'Abzug',
  deductionPlural: 'Abzüge',
  party: 'Firma',
  partyPlural: 'Firmen',
  template: COMMON.template,
  paymentPlan: 'Zahlungsplan',
  paymentPlanPlural: 'Zahlungspläne',
  planningFee: 'Planungspauschale',
  /** Where the invoices of a party no contract covers are booked: the name
   *  of a DIRECT contract, its title, and the block it is listed under. */
  withoutContract: 'Ohne Vertrag',
} as const

/**
 * The stages money goes through, named once: the tab's figures, an
 * installment's columns and state, and the stamp all say them.
 */
export const FINANCE_FIGURES = {
  planned: 'Geplant',
  due: 'Fällig',
  issued: 'Gestellt',
  /** What a contract has been invoiced for in all. */
  invoiced: 'Abgerechnet',
  /** Accepted by the check, before the deductions. */
  accepted: 'Anerkannt',
  /** Released for payment: accepted less the deductions. */
  approved: 'Freigegeben',
  paid: 'Bezahlt',
  /** Issued less every deduction, taken or still to come, less payments. */
  owed: 'Offen',
  /** Released by a check and not yet paid: what a payment goes against. */
  open: 'Offen (freigegeben)',
  retained: 'Einbehalten',
  /** The cents a payment missed what was released by, settled all the same. */
  rounding: 'Rundungsdifferenz',
} as const

/** What an approval leaves to be paid, on either side: the figure the
 *  Prüfvermerk puts under the deductions. */
export const PAYABLE_LABEL = 'Zahlbetrag'

/** When an installment or a retention falls due: with a milestone. */
const DUE_WITH = 'Fällig mit'

/** The things a dialog may start from a PDF of. */
export type DocumentFirstKind = Extract<DocumentTag, 'INVOICE' | 'CONTRACT'>

type DocumentFirstWords = {
  drop: string
  without: string
  reading: string
  readNothing: string
}

/** A dialog that starts from a PDF: the dropzone, the way round it, and
 *  what the reading reports; `byKind` holds what names the thing. */
export const DOCUMENT_FIRST = {
  dropHint: 'Die Felder werden aus dem PDF vorbelegt und bleiben editierbar.',
  pickExisting: 'Oder ein PDF aus den Projektdokumenten wählen',
  noDocument: 'Kein PDF hinterlegt',
  upload: 'PDF hochladen',
  read: 'Aus PDF auslesen',
  readDone: 'Felder aus dem PDF vorbelegt. Bitte prüfen.',
  /** The reading itself broke; says nothing about what the PDF holds. */
  readFailed:
    'Das PDF konnte nicht ausgelesen werden. Bitte Felder von Hand ausfüllen.',
  byKind: {
    INVOICE: {
      drop: 'Rechnungs-PDF hier ablegen',
      without: 'Ohne PDF erfassen',
      reading: 'Rechnung wird gelesen …',
      readNothing:
        'Im PDF wurde keine Rechnung erkannt. Bitte Felder von Hand ausfüllen.',
    },
    CONTRACT: {
      drop: 'Vertrags-PDF hier ablegen',
      without: 'Ohne PDF anlegen',
      reading: 'Vertrag wird gelesen …',
      readNothing:
        'Im PDF wurde kein Vertrag erkannt. Bitte Felder von Hand ausfüllen.',
    },
  } satisfies Record<DocumentFirstKind, DocumentFirstWords>,
} as const

export const FINANCE = {
  /** The two sides of a project's money: the Kunde the project is built
   *  for, the Nachunternehmer it is built with. */
  sides: {
    RECEIVABLE: TERMS.customer,
    PAYABLE: TERMS.subcontractor,
  } satisfies Record<ContractDirection, string>,
  /** A net figure's gross beside it. */
  gross: (amount: string) => `${amount} brutto`,
  /** Under a money column's head whose cells stack the two figures. */
  netGrossHead: 'netto · brutto',

  /** The band over the tab: what the project sells for, buys for, and
   *  what has moved on each side. */
  head: {
    receivable: 'Auftragssumme',
    payable: 'Vergabesumme',
    received: `Erhalten vom ${TERMS.customer}n`,
    /** Released on the customer's invoices and not yet paid. */
    receivableOpen: `Offen vom ${TERMS.customer}n`,
    paidOut: `Gezahlt an ${TERMS.subcontractor}`,
    marginContracted: 'Marge Soll',
    marginForecast: 'Marge Prognose',
    marginCash: 'Marge Zahlungsstand',
    overdue: (n: number) =>
      n === 1 ? '1 Rechnung überfällig' : `${n} Rechnungen überfällig`,
  },

  /** What is waiting on somebody: the list at the head of the overview. */
  queue: {
    title: 'Handlungsbedarf',
    review: 'Zu prüfen',
    overdue: 'Überfällig',
    billable: 'Abrechenbar',
    receivedOn: (date: string) => `eingegangen am ${date}`,
    dueOn: (date: string) => `fällig am ${date}`,
    overdueFor: (days: number) =>
      days === 1 ? 'seit 1 Tag' : `seit ${days} Tagen`,
    reachedOn: (milestone: string, date: string) =>
      `${milestone} erreicht am ${date}`,
    notInvoiced: 'noch nicht in Rechnung gestellt',
    notPaid: 'noch nicht gezahlt',
    releasedOnBy: (date: string, name: string) =>
      `freigegeben am ${date} von ${name}`,
    empty: 'Nichts zu tun. Alle Rechnungen sind geprüft und bezahlt.',
  },

  milestones: {
    title: FINANCE_TERMS.milestonePlural,
    reach: 'Erreicht melden',
    unreach: 'Zurücknehmen',
    reachedOf: (reached: number, total: number) =>
      `${reached} von ${total} erreicht`,
    /** What reaching the next milestone makes billable. */
    unlocks: (installments: string, amount: string) =>
      `Damit wird ${installments} abrechenbar, ${amount}.`,
    unlocksNothing: 'Kein Abschlag hängt an diesem Meilenstein.',
    notReached: 'Ausstehend',
    add: `${FINANCE_TERMS.milestone} hinzufügen`,
    edit: `${FINANCE_TERMS.milestone} bearbeiten`,
    remove: `${FINANCE_TERMS.milestone} entfernen`,
    removeQuestion: (title: string) =>
      `„${title}“ aus dem Bauablauf entfernen?`,
    notUnderConstruction:
      'Das Projekt ist noch nicht im Bau. Der Meilenstein wird trotzdem als erreicht gespeichert.',
    customTitleHint:
      'Ohne Code: keine Vorlage kann auf diesen Meilenstein zeigen.',
    empty:
      'Noch keine Meilensteine. Die Vorlage unter Einstellungen füllt neue Projekte.',
  },

  contracts: {
    /** Either kind: the dialog it opens asks which. */
    add: `${FINANCE_TERMS.contract}/${FINANCE_TERMS.supplement} erfassen`,
    pick: `${FINANCE_TERMS.contract} wählen`,
    /** On a contract's line in the overview. */
    unbalanced: `${FINANCE_TERMS.paymentPlan} ≠ Vertragssumme`,
    addSupplement: `${FINANCE_TERMS.supplement} anlegen`,
    title: FINANCE_TERMS.contractPlural,
    edit: `${FINANCE_TERMS.contract} bearbeiten`,
    remove: `${FINANCE_TERMS.contract} löschen`,
    removeQuestion: (title: string) =>
      `„${title}“ löschen? Abschläge und Abzüge gehen mit.`,
    /** Under a side's heading in the overview while it has no contract. */
    emptySide: {
      RECEIVABLE: 'Noch kein Vertrag.',
      PAYABLE: 'Noch kein Auftrag.',
    } satisfies Record<ContractDirection, string>,
    direction: 'Richtung',
    kind: 'Vertragsart',
    fromTemplate: 'Aus Vorlage',
    noTemplate: `Ohne ${FINANCE_TERMS.template}`,
    prefillFromPositions: 'Summe aus freigegebenen Positionen übernehmen',
    prefillFromUnassigned:
      'Summe aus noch nicht zugeordneten Positionen übernehmen',
    lockConfigurator: `${TERMS.configurator} sperren`,
    lockConfiguratorHint:
      'Nach Vertragsschluss ändert niemand mehr die Planung; Änderungen werden ein Nachtrag.',
    planUnbalanced: (plan: string, net: string) =>
      `Die Abschläge ergeben ${plan}, die Vertragssumme ${net}. Bitte angleichen.`,
    sum: 'Vertragssumme',
    plan: FINANCE_TERMS.paymentPlan,
    document: 'Vertrags-PDF',
    openDocument: 'Vertrag öffnen',
    fromDocument: 'Aus dem PDF gelesen',
    planFromDocument: (installments: number, deductions: number) =>
      `${installments === 1 ? '1 Abschlag' : `${installments} Abschläge`} und ${deductions === 1 ? '1 Abzug' : `${deductions} Abzüge`} aus dem PDF. Nach dem Anlegen im Zahlungsplan änderbar.`,
    extraFeesFromDocument: (fees: string) =>
      `Zusätzlich laut PDF, nicht angerechnet: ${fees}. Bei Anfall als Nachtrag anlegen.`,
    /** A DIRECT contract wherever one name has to say it all: the picker,
     *  the strip. */
    directName: (party: string) =>
      `${FINANCE_TERMS.withoutContract} · ${party}`,
    /** What a status other than in force means for the figures. */
    statusHint: {
      ACTIVE: null,
      DRAFT: 'Noch nicht beauftragt: kein Abschlag ist fällig.',
      CANCELLED: 'Aufgehoben: zählt in keiner Summe.',
    } satisfies Record<ContractStatus, string | null>,
  },

  installments: {
    title: FINANCE_TERMS.installmentPlural,
    add: `${FINANCE_TERMS.installment} hinzufügen`,
    edit: `${FINANCE_TERMS.installment} bearbeiten`,
    remove: `${FINANCE_TERMS.installment} entfernen`,
    removeQuestion: (title: string) => `Abschlag „${title}“ entfernen?`,
    empty:
      'Kein Zahlungsplan: Rechnungen werden auf die Vertragssumme gebucht.',
    dueWith: DUE_WITH,
    dueOn: 'Fällig am',
    dueImmediately: 'Sofort fällig',
    asPercent: 'Prozent der Vertragssumme',
    /** The Planungspauschale above the plan. */
    addPlanningFee: `${FINANCE_TERMS.planningFee} hinzufügen`,
    editPlanningFee: `${FINANCE_TERMS.planningFee} bearbeiten`,
    removePlanningFee: `${FINANCE_TERMS.planningFee} entfernen`,
    planningFeeHint:
      'Steht über den Abschlägen und zählt nicht zu den 100 % der Vertragssumme.',
    planningFeeDefault: (percent: string) =>
      `Vorbelegt mit ${percent} der Vertragssumme, frei änderbar.`,
    creditedWith: 'Verrechnet mit',
    creditedAgainst: (title: string) => `Verrechnet mit „${title}“`,
    notCredited: 'Nicht verrechnet, zusätzlich zur Vertragssumme',
    reducedBy: (fee: string) => `abzüglich ${fee} ${FINANCE_TERMS.planningFee}`,
  },

  /** The size of a share of a contract — an installment, a deduction: a
   *  percent or a fixed sum. What the percent is of, each says itself. */
  figure: {
    label: 'Höhe',
    asAmount: 'Fester Betrag',
  },

  deductions: {
    title: FINANCE_TERMS.deductionPlural,
    add: `${FINANCE_TERMS.deduction} hinzufügen`,
    edit: `${FINANCE_TERMS.deduction} bearbeiten`,
    remove: `${FINANCE_TERMS.deduction} entfernen`,
    removeQuestion: (title: string) => `Abzug „${title}“ entfernen?`,
    empty: 'Keine Abzüge.',
    releasedWith: DUE_WITH,
    ofEachInvoice: 'von jeder Rechnung',
    /** Where a contract-wide one is taken: a share or a fixed sum, from the
     *  first approvals or at one installment. */
    ofContract: (fixed: boolean, at: string | null) =>
      `${fixed ? 'einmalig' : 'der Vertragssumme'}, ${at ? `bei „${at}“` : 'ab der ersten Freigabe'}`,
    /** Over the choice of basis and the choice of kind. */
    basis: 'Bezug',
    kind: 'Art',
    asPercent: 'Prozent',
    /** A fixed sum is one total, so it has no per-invoice form. */
    fixedIsContractWide:
      'Ein fester Betrag gilt einmal für den ganzen Vertrag.',
    deductAt: 'Abziehen bei',
    deductAtFirst: 'Ab der ersten Freigabe',
  },

  invoices: {
    title: FINANCE_TERMS.invoicePlural,
    add: `${FINANCE_TERMS.invoice} erfassen`,
    /** Writes the customer's invoice, where `add` records one received. */
    create: `${FINANCE_TERMS.invoice} erstellen`,
    edit: `${FINANCE_TERMS.invoice} bearbeiten`,
    remove: `${FINANCE_TERMS.invoice} löschen`,
    removeQuestion: (number: string, approved: boolean) =>
      `Rechnung ${number} löschen?${approved ? ' Die Freigabe wird mit entfernt.' : ''}`,
    cancel: 'Stornieren',
    cancelQuestion: (number: string) =>
      `Rechnung ${number} stornieren? Sie bleibt sichtbar und zählt nirgends mehr mit.`,
    replacedBy: (number: string) => `Ersetzt durch ${number}`,
    /** A document raising or lowering an invoice already issued. */
    addCorrection: 'Rechnungskorrektur erfassen',
    correctionOf: (number: string) => `Rechnungskorrektur zu ${number}`,
    empty: 'Noch keine Rechnung.',
    document: 'Rechnungs-PDF',
    readIssuer: (issuer: string) => `Rechnungssteller laut PDF: ${issuer}`,
    /** The sender read off the PDF is a Firma on file and was picked. */
    readPartyMatched: `${FINANCE_TERMS.party} erkannt`,
    open: `${FINANCE_TERMS.invoice} öffnen`,
    /** The two copies the viewer switches between. */
    original: 'Original',
    stamped: 'Mit Prüfvermerk',
    openInTab: 'In neuem Tab öffnen',
    stampPosition: 'Prüfvermerk (Stempel) platzieren',
    stampHint:
      'Den Prüfvermerk so verschieben, dass er nichts Wichtiges verdeckt.',
    stampScale: 'Größe des Prüfvermerks',
    allocation: 'Zugeordnete Abschläge',
    /** The head over an unfolded invoice's lines. */
    allocationLine: `Zugeordneter ${FINANCE_TERMS.installment}`,
    number: 'Rechnungsnr.',
    count: (n: number) => (n === 1 ? '1 Rechnung' : `${n} Rechnungen`),
    allocationHint:
      'Welche Abschläge des Zahlungsplans diese Rechnung abrechnet, und mit welchem Teil des Nettobetrags.',
    /** A line booked against the contract as a whole, not an installment. */
    wholeContract: 'Auf die Vertragssumme',
    retentionRelease: 'Sicherheitseinbehalt',
    addLine: 'Zeile hinzufügen',
    lineHasApprovals: 'Freigegeben: Betrag kann nur erhöht werden.',
    creditNoteHint:
      'Negativer Betrag: eine Gutschrift gegen denselben Vertrag.',
    termDays: (n: number) => (n === 1 ? '1 Tag' : `${n} Tage`),
    /** An invoice no contract covers, booked on the side's and the Firma's
     *  DIRECT contract. */
    side: 'Seite',
    /** Approve on create. */
    approveOnSave: 'Direkt freigeben',
    approveOnSaveHint: 'Die Rechnung wird direkt zur Zahlung freigegeben.',
    approveBlocked:
      'Nicht möglich: eine Zeile ist gekürzt oder rechnet mehr ab, als der Abschlag zulässt.',
    move: 'In anderen Vertrag verschieben',
    moveTitle: (number: string) => `Rechnung ${number} verschieben`,
    moveTarget: `Ziel-${FINANCE_TERMS.contract}`,
    moveHint:
      'Freigaben, Abzüge und Zahlungen gehen unverändert mit; ein Abzug des alten Vertrags verliert nur die Verknüpfung.',
    moveWithCorrections: (n: number) =>
      n === 1
        ? 'Die Rechnungskorrektur wird mit verschoben.'
        : `Die ${n} Rechnungskorrekturen werden mit verschoben.`,
    moveLines: 'Zeilen im Ziel',
  },

  approvals: {
    /** The column under an invoice that says who approved a line, when. */
    title: 'Freigabe',
    /** The button on an invoice still waiting, and the dialog it opens:
     *  the check may also cut. */
    approve: 'Prüfen und freigeben',
    approveLine: (title: string) => `${title} freigeben`,
    amountHint: (left: string) => `Noch offen auf dieser Zeile: ${left}`,
    deductionsTitle: 'Abzüge auf diese Freigabe',
    extraDeduction: 'Weiterer Abzug',
    addExtra: 'Abzug hinzufügen',
    payable: PAYABLE_LABEL,
    remove: 'Freigabe zurücknehmen',
    /** Under the line, ahead of who approved it. */
    approvedOnBy: (date: string) => `Freigegeben am ${date} von`,
    /** A line approved in part: how much of it was accepted. */
    acceptedOf: (part: string, whole: string) =>
      `${part} von ${whole} anerkannt`,
    removeOn: (date: string) => `Freigabe vom ${date} zurücknehmen`,
    /** After a deduction the check to come would take. */
    expected: 'erwartet',
    removeQuestion: 'Diese Freigabe zurücknehmen?',
  },

  payments: {
    received: 'Zahlungseingang erfassen',
    made: 'Zahlung erfassen',
    /** What is open is owed back: a correction lowered a paid invoice. */
    refund: 'Rückzahlung erfassen',
    openHint: (open: string) => `${FINANCE_FIGURES.open}: ${open}`,
    /** Under the amount, where it misses what is open by rounding only. */
    roundingHint: (difference: string) =>
      `${difference} Abweichung, wird als ${FINANCE_FIGURES.rounding} ausgeglichen`,
    /** Beside the open figure of an invoice a payment settled that way. */
    roundingSettled: (difference: string) =>
      `${FINANCE_FIGURES.rounding} ${difference} ausgeglichen`,
    remove: 'Zahlung löschen',
    /** A payment that also settled corrections goes from all of them. */
    removeQuestion: (shared: boolean) =>
      shared
        ? 'Diese Zahlung löschen? Sie wird auch von den Rechnungskorrekturen entfernt, die sie beglichen hat.'
        : 'Diese Zahlung löschen?',
    /** Before the whole transfer, under an invoice's share of a payment that
     *  settled more than it. */
    partOf: 'Teil einer Zahlung über',
    title: FINANCE_TERMS.paymentPlural,
    empty: 'Noch keine Zahlung.',
    recordedBy: 'Erfasst von',
    /** The first dunning step. Offered, not yet sent by the app. */
    remind: 'Zahlungserinnerung senden',
    remindSoon: 'Kommt noch: die Erinnerung geht später aus der App heraus.',
  },

  parties: {
    title: FINANCE_TERMS.partyPlural,
    subtitle:
      'Gewerke, Generalunternehmer und Architekten, projektübergreifend.',
    add: `${FINANCE_TERMS.party} anlegen`,
    edit: `${FINANCE_TERMS.party} bearbeiten`,
    remove: `${FINANCE_TERMS.party} löschen`,
    removeQuestion: (name: string) => `„${name}“ löschen?`,
    empty: 'Noch keine Firma angelegt.',
    defaultDeductions: 'Abzüge auf jeder Rechnung',
    contractCount: (n: number) => (n === 1 ? '1 Vertrag' : `${n} Verträge`),
    pick: 'Firma wählen',
    /** The party a PDF names, which no Firma on file matched. */
    readUnknown: (name: string) =>
      `Laut PDF: „${name}“, im Bestand nicht gefunden.`,
    /** Makes a Firma of the party a PDF names. */
    addFromDocument: `Als ${FINANCE_TERMS.party} anlegen`,
    noTerm: 'Kein Zahlungsziel',
  },

  templates: {
    title: FINANCE_TERMS.paymentPlanPlural,
    subtitle:
      'Die Abschläge und Abzüge, mit denen ein neuer Vertrag beginnt. Abschläge zeigen per Code auf die Meilensteine.',
    add: `${FINANCE_TERMS.template} anlegen`,
    edit: `${FINANCE_TERMS.template} bearbeiten`,
    remove: `${FINANCE_TERMS.template} löschen`,
    removeQuestion: (title: string) => `Vorlage „${title}“ löschen?`,
    empty: 'Noch keine Vorlage.',
    milestones: {
      title: 'Bauablauf',
      subtitle:
        'Die Meilensteine, die jedes neue Projekt bekommt. Der Code bleibt beim Umbenennen erhalten.',
      code: 'Code',
    },
  },

  warnings: {
    notUnderConstruction: 'Meilenstein erreicht, Projekt nicht im Bau',
  },

  errors: {
    partyRequired: 'Ein Auftrag braucht eine Firma.',
    partyNotAllowed:
      'Ein Kundenvertrag gehört dem Kunden und nennt keine Firma.',
    partyStillUsed: (count: number) =>
      count === 1
        ? 'Die Firma wird noch in einem Vertrag genannt.'
        : `Die Firma wird noch in ${count} Verträgen genannt.`,
    parentMismatch:
      'Ein Nachtrag gehört zu einem Hauptvertrag desselben Projekts und derselben Richtung.',
    hasInvoices: (count: number) =>
      count === 1
        ? 'Auf dem Vertrag steht noch eine Rechnung. Bitte stornieren statt löschen.'
        : `Auf dem Vertrag stehen noch ${count} Rechnungen. Bitte stornieren statt löschen.`,
    hasSupplements: (count: number) =>
      count === 1
        ? 'Zum Vertrag gehört noch ein Nachtrag. Bitte zuerst den Nachtrag löschen.'
        : `Zum Vertrag gehören noch ${count} Nachträge. Bitte zuerst die Nachträge löschen.`,
    templateMismatch:
      'Die Vorlage gehört zur anderen Richtung (Einnahme oder Ausgabe).',
    milestoneNotInProject: 'Der Meilenstein gehört zu einem anderen Projekt.',
    allocationSignMismatch:
      'Eine Zuordnungszeile trägt das Vorzeichen ihrer Rechnung.',
    deductionPartyPerInvoiceOnly:
      'Ein Abzug einer Firma gilt je Rechnung; ein Anteil der Vertragssumme wird am Vertrag angelegt.',
    invoiceHasCorrections: (action: 'cancel' | 'delete') =>
      action === 'cancel'
        ? 'An der Rechnung hängen Rechnungskorrekturen. Erst die Korrekturen stornieren.'
        : 'An der Rechnung hängen Rechnungskorrekturen, auch stornierte. Erst die Korrekturen löschen.',
    installmentHasDeduction: (title: string) =>
      `Der Abzug „${title}“ wird bei diesem Abschlag abgezogen. Erst den Abzug ändern.`,
    correctionTarget:
      'Eine Rechnungskorrektur gehört zu einer gültigen Rechnung desselben Vertrags, nicht zu einer anderen Korrektur.',
    deductionAmountOfContractOnly:
      'Ein fester Betrag gilt für den ganzen Vertrag, nicht je Rechnung.',
    deductionNeedsOneFigure:
      'Ein Abzug ist entweder ein Prozentsatz oder ein Betrag.',
    milestoneHasInvoiced: (count: number) =>
      `Auf Abschläge dieses Meilensteins ${count === 1 ? 'ist bereits eine Rechnung gestellt' : `sind bereits ${count} Rechnungen gestellt`}.`,
    milestoneReleasesRetention: (count: number) =>
      count === 1
        ? 'Ein Sicherheitseinbehalt wird mit diesem Meilenstein fällig. Bitte zuerst den Abzug umhängen.'
        : `${count} Sicherheitseinbehalte werden mit diesem Meilenstein fällig. Bitte zuerst die Abzüge umhängen.`,
    milestoneCodeTaken: (code: string) =>
      `Der Code „${code}“ ist bereits vergeben.`,
    installmentInvoiced:
      'Der Abschlag ist bereits in Rechnung gestellt und kann nicht gelöscht werden.',
    installmentNeedsOneFigure:
      'Ein Abschlag ist entweder ein Prozentsatz oder ein Betrag.',
    installmentNotInContract: 'Der Abschlag gehört zu einem anderen Vertrag.',
    retentionWithoutContract: `${FINANCE_TERMS.withoutContract} wird kein Sicherheitseinbehalt abgerufen.`,
    installmentCreditUnknown:
      'Nur die Planungspauschale wird verrechnet, und nur mit einem Abschlag desselben Vertrags.',
    planningFeeNotAllowed:
      'Eine Planungspauschale gibt es nur am Hauptvertrag mit dem Kunden, und nur eine.',
    planningFeeExceedsInstallment:
      'Die Planungspauschale ist höher als der Abschlag, mit dem sie verrechnet wird.',
    installmentCredited:
      'Mit diesem Abschlag wird die Planungspauschale verrechnet. Erst die Verrechnung ändern.',
    invoiceCancelled: 'Die Rechnung ist storniert.',
    invoiceNotDeletable:
      'Auf der Rechnung stehen Zahlungen. Eine bezahlte Rechnung wird per Gutschrift korrigiert.',
    invoiceHasPayments: (count: number) =>
      count === 1
        ? 'Auf der Rechnung steht eine Zahlung. Eine bezahlte Rechnung wird per Gutschrift korrigiert, nicht storniert.'
        : `Auf der Rechnung stehen ${count} Zahlungen. Eine bezahlte Rechnung wird per Gutschrift korrigiert, nicht storniert.`,
    invoiceNotStamped:
      'Für diese Rechnung gibt es noch keine Kopie mit Prüfvermerk.',
    invoiceHasNoDocument: 'Die Rechnung hat kein PDF.',
    allocationExceedsInvoice: (allocated: string, issued: string) =>
      `Die abgerechneten Abschläge (${allocated}) übersteigen den Rechnungsbetrag (${issued}).`,
    allocationHasApprovals:
      'Eine freigegebene Zeile kann nicht entfernt oder unter die Freigabe gekürzt werden.',
    approvalExceedsLine: (left: string) =>
      `Auf dieser Zeile sind nur noch ${left} freizugeben.`,
    approvalDeductsMoreThanApproved:
      'Die Abzüge übersteigen den freigegebenen Betrag.',
    paymentExceedsOpen: (open: string) =>
      `${FINANCE_FIGURES.open} sind nur ${open}.`,
    directFixed:
      'Eine Rechnung ohne Vertrag hat keine Vertragssumme, keinen Zahlungsplan und keine Abzüge.',
    approveOverInstallment:
      'Die Rechnung rechnet mehr ab, als der Abschlag zulässt, und kann nicht beim Erfassen freigegeben werden.',
    approveLineCut:
      'Eine Zeile rechnet weniger ab, als der Abschlag offen hat, und kann nicht beim Erfassen freigegeben werden.',
    moveCorrection:
      'Eine Rechnungskorrektur wird mit ihrer Rechnung verschoben.',
    moveSameContract: 'Die Rechnung steht bereits auf diesem Vertrag.',
    moveDirection:
      'Eine Rechnung wird nur in einen Vertrag derselben Seite verschoben.',
    moveRetentionRelease:
      'Eine Rechnung, die einen Sicherheitseinbehalt abruft, wird nicht verschoben.',
    moveRetentionHeld: `Auf der Rechnung ist ein Sicherheitseinbehalt einbehalten. ${FINANCE_TERMS.withoutContract} kann er nicht abgerufen werden, deshalb bleibt sie bei einem Vertrag.`,
    moveCancelledContract:
      'Auf einen aufgehobenen Vertrag wird keine Rechnung verschoben.',
    retentionExceeded: (invoicedBack: string, held: string) =>
      `Damit wären ${invoicedBack} Sicherheitseinbehalt abgerufen, einbehalten sind aber nur ${held} netto. Einbehalten ist, was freigegeben ist.`,
    moveOverInstallment:
      'Der Abschlag im Zielvertrag hat dafür nicht mehr genug offen. Wähle einen anderen Abschlag oder die Vertragssumme.',
    moveBankPayment:
      'Auf der Rechnung steht eine Zahlung aus einer Banküberweisung. Sie kann nicht zu einer anderen Firma verschoben werden.',
    moveSharedPayment:
      'Eine Zahlung auf der Rechnung begleicht auch Rechnungen, die nicht mit verschoben werden.',
    moveLineUnknown: 'Die Zeile gehört nicht zu dieser Rechnung.',
  },
} as const

/** The customer's wording, on the screen and on the customer PDF. */
export const CUSTOMER_FINANCE = {
  progressTitle: 'Baufortschritt',
  progressSubtitle: 'Die Phasen deines Projekts, und welche erreicht sind.',
  paymentsTitle: 'Deine Zahlungen',
  paymentsSubtitle:
    'Die Abschlagszahlungen deines Vertrags, fällig mit dem Baufortschritt.',
  reachedOn: (date: string) => `Erreicht am ${date}`,
  pending: FINANCE.milestones.notReached,
  due: FINANCE_FIGURES.due,
  invoiced: 'Rechnung gestellt',
  invoicedOn: (date: string) => `Rechnung vom ${date}`,
  payUntil: (date: string) => `Bitte zahlen bis ${date}`,
  paid: FINANCE_FIGURES.paid,
  overdue: 'Überfällig',
  retentionHint: 'Wird mit der Abnahme fällig.',
  openNotice: (n: number, sum: string) =>
    n === 1
      ? `Eine Rechnung über ${sum} inkl. USt. ist offen.`
      : `${n} Rechnungen über zusammen ${sum} inkl. USt. sind offen.`,
  overdueNotice: (n: number) =>
    n === 1
      ? 'Eine Rechnung ist überfällig.'
      : `${n} Rechnungen sind überfällig.`,
  plusVat: (rate: string) => `zzgl. ${rate} USt.`,
  /** Invoices outside the payment plan, under it. */
  otherInvoicesTitle: 'Weitere Rechnungen',
  otherInvoicesSubtitle:
    'Rechnungen außerhalb des Zahlungsplans, nicht Teil der Auftragssumme.',
  invoiceNumbered: (number: string) => `Rechnung ${number}`,
  /** Where an invoice with a negative amount stands: nothing to pay. */
  creditNote: 'Gutschrift',
} as const

/** What the stamped copy of an invoice says. */
export const STAMP = {
  title: 'Prüfvermerk BrickUp',
  invoice: (number: string) => `Rechnung ${number}`,
  issued: 'Rechnungsbetrag netto',
  approved: FINANCE_FIGURES.accepted,
  deduction: (title: string) => `Abzug ${title}`,
  payable: PAYABLE_LABEL,
  /** Under the sections of several invoices on one PDF. */
  payableTotal: `${PAYABLE_LABEL} gesamt`,
  paid: (date: string) => `Gezahlt am ${date}`,
  open: FINANCE_FIGURES.open,
  approvedBy: (name: string, date: string) =>
    `Freigegeben von ${name} am ${date}`,
  cancelled: (date: string) => `STORNIERT am ${date}`,
  generatedAt: (date: string) => `Stand ${date}`,
  none: COMMON.notSpecified,
} as const
